import _ from "lodash";

import { isNotUndefined } from "utils/common";

import {
  generateCellItemKey,
  generateRowItemKey,
} from "entities/AssetManagement/components/Table/utils";
import { AMColumn, AMResolution } from "entities/AssetManagement/constants";
import {
  IAssetManagementDataV2,
  IAssetManagementRows,
} from "entities/AssetManagement/sdk";

export const getTypeAndDateInfoByColumnKey = (
  columnKey: string
): { year: number; type: string; quarter?: number; month?: number } => {
  const [year, quarter, month, type] = columnKey.split("-");

  if (!_.isNil(quarter)) {
    return {
      year: Number(year),
      quarter: Number(quarter),
      type,
    };
  }

  if (!_.isNil(month)) {
    return {
      year: Number(year),
      month: Number(month),
      type,
    };
  }

  return { year: Number(year), type };
};

export const constructUpdatedDict = ({
  tableData,
  changedCells,
  diffRows,
  deletedRowKeys,
  yearMappings,
}: {
  tableData: IAssetManagementRows;
  changedCells: {
    [key: string]: {
      previous_value: number | null;
      current_value: number | null;
    };
  };
  diffRows?: {
    [key: string]: {
      is_deleted: boolean;
      order: number | null;
      previous_label: string | null;
      current_label: string | null;
    };
  };
  deletedRowKeys: Array<string>;
  yearMappings: {
    [key: string]: string;
  };
}): Array<{
  key: string;
  label: string;
  values: Array<{
    key: string;
    value: number | null;
  }>;
}> => {
  let newCells = changedCells; // We'll remove the updated ones later on.

  // Construct the diff object containing only the changed cells.
  let diff = _.filter(
    tableData.rows,
    (row) => !row.is_total_row && !row.is_calculation_item
  ).map((row) => {
    const rowKey = generateRowItemKey({ row });

    return {
      key: rowKey,
      label: row.name,
      values: _.map(row.values, (value) => {
        const valueKey = generateCellItemKey({ row, value });
        let cellValue = value.value;

        if (!_.isUndefined(changedCells[valueKey])) {
          cellValue = changedCells[valueKey].current_value;
          newCells = _.omit(newCells, [valueKey]);
        }

        return {
          key: valueKey,
          value: cellValue,
        };
      }),
    };
  });

  // Construct the object containing only the new rows with changed cells.
  let newRows = _.map(diffRows, (row, rowKey) => {
    // We want to iterate only the rows that are new and have changed cells.

    // TODO: upgrade this code
    if (_.isUndefined(_.find(diff, (tableRow) => tableRow.key === rowKey))) {
      return {
        key: rowKey,
        label: row.current_label || "",
        values: _.map(changedCells, (value, key) => {
          if (key.startsWith(rowKey)) {
            let cellValue = value.current_value;

            if (!_.isUndefined(newCells) && !_.isUndefined(newCells[key])) {
              cellValue = newCells[key].current_value;
              newCells = _.omit(newCells, [key]);
              return {
                key,
                value: cellValue,
              };
            }
          }
        }).filter(isNotUndefined),
      };
    }
  }).filter(isNotUndefined);

  // Add the new cells to the diff.
  _.forEach(newCells, (newCell, key) => {
    const [itemCode, categoryId, subCategoryName] = key.split("|");

    diff = diff.map((row) =>
      row.key === `${itemCode}|${categoryId}|${subCategoryName}`
        ? {
            ...row,
            values: [
              ...row.values,
              {
                key,
                value: newCell.current_value,
              },
            ],
          }
        : row
    );
  });

  diff = [...diff, ...newRows];
  // Remove the deleted rows from the diff.
  diff = diff.filter((row) => !deletedRowKeys.includes(row.key));

  // Apply year mappings over the final diff.
  diff = diff.map((row) => ({
    ...row,
    values: row.values.map(({ value, key }) => {
      const [
        itemCode,
        categoryId,
        subCategoryName,
        year,
        quarter,
        month,
        dataType,
      ] = key.split("|");

      const newYear = yearMappings[year] || year;
      const newKey = `${itemCode}|${categoryId}|${subCategoryName}|${newYear}|${quarter}|${month}|${dataType}`;

      return { value, key: newKey };
    }),
  }));

  return diff;
};

export const extractLatestDatasetByAMColumn = ({
  data,
  resolution,
  formatter,
}: {
  resolution: AMResolution;
  formatter: (info: {
    year: number;
    quarter?: number;
    month?: number;
  }) => string;
  data?: IAssetManagementRows;
}) => {
  if (_.isNil(data?.rows)) {
    return {
      audited: "No data",
      budgeted: "No data",
      actual: "No data",
    };
  }

  const allValues = data.rows.flatMap((row) => row.values);

  const findLatestForAMColumn = ({ columnName }: { columnName: AMColumn }) => {
    if (
      resolution === AMResolution.MONTHLY &&
      columnName === AMColumn.AUDITED
    ) {
      return "No datasets";
    }

    const filtered = allValues.filter((v) => {
      const { year, quarter, month } = v.date_info;
      if (v.data_type !== columnName || _.isNil(year)) return false;

      if (resolution === AMResolution.YEARLY)
        return _.isNil(quarter) && _.isNil(month);
      else if (resolution === AMResolution.QUARTERLY)
        return !_.isNil(quarter) && _.isNil(month);
      else if (resolution === AMResolution.MONTHLY)
        return !_.isNil(month) && _.isNil(quarter);
      return false;
    });

    if (filtered.length === 0) return "No datasets";

    filtered.sort((a, b) => {
      const yearA = Number(a.date_info.year);
      const yearB = Number(b.date_info.year);
      if (yearB !== yearA) {
        return yearB - yearA;
      }

      if (resolution === AMResolution.QUARTERLY) {
        return Number(b.date_info.quarter) - Number(a.date_info.quarter);
      }

      if (resolution === AMResolution.MONTHLY) {
        return Number(b.date_info.month) - Number(a.date_info.month);
      }

      return 0;
    });

    const latest = filtered[0];
    return formatter({
      year: Number(latest.date_info.year),
      quarter: latest.date_info.quarter
        ? Number(latest.date_info.quarter)
        : undefined,
      month: latest.date_info.month
        ? Number(latest.date_info.month)
        : undefined,
    });
  };

  return {
    audited: findLatestForAMColumn({ columnName: AMColumn.AUDITED }),
    budgeted: findLatestForAMColumn({ columnName: AMColumn.BUDGETED }),
    actual: findLatestForAMColumn({ columnName: AMColumn.ACTUAL }),
  };
};

// With this function we extract either:
//    1. all rows + totalRow for each subCategory
//    2. calculationItem data, in cases when the subCategory acts as a CalculationItem
export const generateAllRowsFromNewAMDataStructure = ({
  data,
  editMode = false,
}: {
  data?: IAssetManagementDataV2;
  editMode?: boolean;
}) => {
  if (_.isNil(data)) return { rows: [] };

  let rows = [];

  // In edit mode we want to keep the old way of calcualting the total rows
  // Therefore we exclude the calculation items and total rows from the data we have fetched
  if (editMode) {
    rows = data.categories.flatMap((category) =>
      category.sub_categories.flatMap((item) => {
        if (!item.is_calculation_item) {
          return [
            ...item.rows.map((row) => ({
              ...row,
              sub_category: {
                id: item.id,
                name: item.name,
                category: { id: category.id, name: category.name },
              },
              is_calculation_item: false,
              is_total_row: false,
            })),
          ];
        } else {
          return [];
        }
      })
    );
  } else {
    rows = data.categories.flatMap((category) =>
      category.sub_categories.flatMap((item) => {
        if (!item.is_calculation_item) {
          // Get all rows + totalRow
          return [
            ...item.rows.map((row) => ({
              ...row,
              sub_category: {
                id: item.id,
                name: item.name,
                category: { id: category.id, name: category.name },
              },
              is_calculation_item: false,
              is_total_row: false,
            })),
            {
              ...item.data,
              sub_category: {
                id: item.id,
                name: item.name,
                category: { id: category.id, name: category.name },
              },
              is_calculation_item: false,
              is_total_row: true,
              sub_category_info_text: item.info_text,
            },
          ];
          // Get calculationItem data
        } else {
          return {
            ...item.data,
            sub_category: {
              id: item.id,
              name: item.name,
              category: { id: category.id, name: category.name },
            },
            is_calculation_item: true,
            is_total_row: false,
            sub_category_info_text: item.info_text,
            values: item.data.values.map((val) => ({
              ...val,
              value: val.is_division_by_zero ? Infinity : val.value,
            })),
          };
        }
      })
    );
  }

  return { rows } as IAssetManagementRows;
};
